/**
 * Treasury guard — runtime validation for the canonical SFN purchase
 * destination.
 * ---------------------------------------------------------------------------
 * Every MEAL2 / MEAL4 purchase must settle to SFN_TREASURY_ADDRESS on the
 * configured Solana cluster. Before any purchase button is enabled we:
 *   1. validate the address offline (base58 alphabet + 32-byte decode)
 *   2. confirm on-chain that the account exists and is owned by the
 *      System Program (a plain SOL wallet, NOT a token/program account)
 *
 * All RPC traffic flows through the privacy-hardened `solanaRpcCall`.
 */
import { SFN_TREASURY_ADDRESS } from "./ecosystem-constants";
import { solanaRpcCall, SOLANA_RPC_URL } from "./solana-rpc";

/** Cluster every treasury settlement is expected on. */
export const SOLANA_CLUSTER = "mainnet-beta" as const;

/** Native System Program — owner of ordinary (non-PDA) SOL wallets. */
export const SYSTEM_PROGRAM_ID = "11111111111111111111111111111111";

const B58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Minimal base58 decoder (no deps) — returns bytes or null when invalid. */
export function base58Decode(input: string): Uint8Array | null {
  if (!input) return null;
  const bytes: number[] = [0];
  for (const ch of input) {
    const value = B58_ALPHABET.indexOf(ch);
    if (value === -1) return null;
    let carry = value;
    for (let i = 0; i < bytes.length; i++) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  // leading zeros
  for (let k = 0; k < input.length && input[k] === "1"; k++) bytes.push(0);
  return new Uint8Array(bytes.reverse());
}

export type AddressCheck = { valid: boolean; reason?: string };

/** Offline structural validation of a Solana address. */
export function validateSolanaAddress(address: string): AddressCheck {
  if (typeof address !== "string" || address.length < 32 || address.length > 44) {
    return { valid: false, reason: "length" };
  }
  const bytes = base58Decode(address);
  if (!bytes) return { valid: false, reason: "base58" };
  if (bytes.length !== 32) return { valid: false, reason: "bytes" };
  return { valid: true };
}

/** Convenience: structural check of the canonical treasury constant. */
export function validateTreasuryAddress(address: string = SFN_TREASURY_ADDRESS): AddressCheck {
  return validateSolanaAddress(address);
}

export type TreasuryStatus = "checking" | "verified" | "degraded" | "invalid";

export type TreasuryVerification = {
  status: TreasuryStatus;
  address: string;
  cluster: typeof SOLANA_CLUSTER;
  owner?: string;
  reason?: string;
};

type AccountInfoResponse = {
  value: { owner?: string; lamports?: number; executable?: boolean } | null;
};

/**
 * Confirm the treasury account exists on-cluster and is a System-Program
 * owned wallet. Network failures degrade (never hard-fail) so a rate-limited
 * public RPC cannot brick the purchase UI — but a structurally wrong or
 * program-owned address always resolves to `invalid`.
 */
export async function verifyTreasuryOnChain(
  address: string = SFN_TREASURY_ADDRESS,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<TreasuryVerification> {
  const base = { address, cluster: SOLANA_CLUSTER } as const;
  const structural = validateSolanaAddress(address);
  if (!structural.valid) {
    return { ...base, status: "invalid", reason: structural.reason };
  }
  if (!SOLANA_RPC_URL.includes("mainnet") && !SOLANA_RPC_URL.includes("http")) {
    return { ...base, status: "degraded", reason: "rpc-unconfigured" };
  }
  try {
    const res = await solanaRpcCall<AccountInfoResponse>(
      "getAccountInfo",
      [address, { encoding: "base64" }],
      { fetchImpl: options.fetchImpl, signal: options.signal },
    );
    const value = res?.value;
    if (!value) return { ...base, status: "invalid", reason: "account-not-found" };
    if (value.executable) return { ...base, status: "invalid", reason: "executable-account" };
    if (value.owner && value.owner !== SYSTEM_PROGRAM_ID) {
      return { ...base, status: "invalid", owner: value.owner, reason: "unexpected-owner" };
    }
    return { ...base, status: "verified", owner: value.owner ?? SYSTEM_PROGRAM_ID };
  } catch {
    return { ...base, status: "degraded", reason: "rpc-unreachable" };
  }
}

/** Purchases are permitted for verified accounts and for RPC-degraded reads. */
export function canPurchase(status: TreasuryStatus): boolean {
  return status === "verified" || status === "degraded";
}

/* ────────────────────────── Explorer helpers ────────────────────────── */

const CLUSTER_QUERY = SOLANA_CLUSTER === "mainnet-beta" ? "" : `?cluster=${SOLANA_CLUSTER}`;

export function explorerTxUrl(signature: string): string {
  return `https://solscan.io/tx/${signature}${CLUSTER_QUERY}`;
}

export function explorerAddressUrl(address: string = SFN_TREASURY_ADDRESS): string {
  return `https://solscan.io/account/${address}${CLUSTER_QUERY}`;
}

/**
 * Deterministic-looking signature for the simulated settlement flow.
 * Replaced by the real signature once the wallet adapter is bound.
 */
export function generateTxSignature(seed = Date.now()): string {
  let x = seed >>> 0 || 1;
  let out = "";
  for (let i = 0; i < 88; i++) {
    x ^= x << 13; x >>>= 0;
    x ^= x >> 17;
    x ^= x << 5; x >>>= 0;
    out += B58_ALPHABET[x % B58_ALPHABET.length];
  }
  return out;
}

export type PurchaseReceipt = {
  token: "MEAL2" | "MEAL4";
  payAmount: string;
  payCurrency: "USDC" | "SOL";
  receiveAmount: string;
  destination: string;
  signature: string;
  cluster: typeof SOLANA_CLUSTER;
  timestamp: string;
};

/** Builds a receipt — destination is ALWAYS the canonical treasury. */
export function buildPurchaseReceipt(input: {
  token: "MEAL2" | "MEAL4";
  payAmount: string;
  payCurrency: "USDC" | "SOL";
  receiveAmount: string;
  seed?: number;
  timestamp?: string;
  /** Real on-chain signature — falls back to a simulated one when absent. */
  signature?: string;
}): PurchaseReceipt {
  return {
    token: input.token,
    payAmount: input.payAmount,
    payCurrency: input.payCurrency,
    receiveAmount: input.receiveAmount,
    destination: SFN_TREASURY_ADDRESS,
    signature: input.signature ?? generateTxSignature(input.seed),
    cluster: SOLANA_CLUSTER,
    timestamp: input.timestamp ?? new Date().toISOString(),
  };
}

/** Canonical machine-readable receipt payload (download / audit trail). */
export function receiptToJson(receipt: PurchaseReceipt): string {
  return JSON.stringify(
    {
      signature: receipt.signature,
      treasury: receipt.destination,
      cluster: receipt.cluster,
      token: receipt.token,
      paid: { amount: receipt.payAmount, currency: receipt.payCurrency },
      received: { amount: receipt.receiveAmount, token: receipt.token },
      timestamp: receipt.timestamp,
      explorer: explorerTxUrl(receipt.signature),
    },
    null,
    2,
  );
}

export function receiptFileName(receipt: PurchaseReceipt): string {
  return `sfn-receipt-${receipt.token}-${receipt.signature.slice(0, 12)}.json`;
}

